import { useApi, PaginatedResponse } from '@/api';
import { Link, TableCellBottom, PageSkeleton } from '@/components';
import {
  useTableProps,
  DropdownFilter,
  TextSearchFilter,
  DataTable,
  Flex,
  WalletAddress,
  CryptoIcon,
  PercentageValue,
  CurrencyValue,
  RelativeDateTime
} from '@blockanalitica/ui';
import {
  TableColumn,
  TableData
} from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { useParams } from 'react-router';

type Liquidation = {
  datetime: string;
  price_change: string;
  wallet_address: string;
  collateral_address: string;
  debt_address: string;
  collateral_amount: string;
  collateral_amount_usd: string;
  debt_amount: string;
  debt_amount_usd: string;
  debt_symbol: string;
  collateral_symbol: string;
  collateral_price: string;
  incentive: string;
  last_activity: string;
};

export default function WalletsAtRiskTable() {
  const { network } = useParams();

  const { pagination, filters, sorting } = useTableProps({
    filtersDebounceTime: 500,
    pageSize: 10,
    initialFilters: {
      textSearch: '',
      selectedCollateralOption: '',
      selectedDebtOption: ''
    },
    initialSorting: 'price_change'
  });

  const [data, error] = useApi<PaginatedResponse<Liquidation[]>>({
    url: '/at-risk/wallets/',
    queryParams: {
      page: pagination.currentPage,
      limit: pagination.pageSize,
      sort: sorting.currentSorting,
      search: filters.filtersApplied['textSearch'],
      collateral_assets:
        filters.filtersApplied['selectedCollateralOption'].toString(),
      debt_assets: filters.filtersApplied['selectedDebtOption'].toString()
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <PageSkeleton />;
  }

  const columns: TableColumn<Liquidation>[] = [
    {
      header: 'Wallet',
      getCell: (row) => (
        <Link
          key={row.wallet_address}
          to={`../wallets/${row.wallet_address}`}
          relative="path">
          <WalletAddress address={row.wallet_address} />
        </Link>
      )
    },
    {
      header: 'Price Change',
      getCell: (row) => <PercentageValue value={row.price_change} />,
      sortingField: 'price_change',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Collateral Liquidated',
      getCell: (row) => (
        <CurrencyValue
          value={row.collateral_amount}
          options={{
            currency: {
              name: row.collateral_symbol,
              address: row.collateral_address,
              network: network
            }
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.collateral_amount_usd}>
          <CurrencyValue
            value={row.collateral_amount_usd}
            options={{ currency: 'USD' }}
          />
        </TableCellBottom>
      ),
      sortingField: 'collateral_amount',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Liquidation Price',
      getCell: (row) => (
        <Flex gap={['3xs']} alignItems={'center'}>
          <CurrencyValue
            value={row.collateral_price}
            options={{ currency: 'USD' }}
          />
          <CryptoIcon
            name={row.collateral_symbol}
            address={row.collateral_address}
            size="1"
            network={network}
          />
        </Flex>
      ),
      sortingField: 'collateral_price',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Debt Repaid',
      getCell: (row) => (
        <CurrencyValue
          value={row.debt_amount}
          options={{
            currency: {
              name: row.debt_symbol,
              address: row.debt_address,
              network: network
            }
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.debt_amount_usd}>
          <CurrencyValue
            value={row.debt_amount_usd}
            options={{ currency: 'USD' }}
          />
        </TableCellBottom>
      ),
      sortingField: 'debt_amount',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Liquidation Bonus',
      getCell: (row) => (
        <CurrencyValue value={row.incentive} options={{ currency: 'USD' }} />
      ),
      sortingField: 'incentive',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Last Activity',
      getCell: (row) => <RelativeDateTime value={row.last_activity} />,
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'last_activity'
    }
  ];

  return (
    <Flex gap="m" flexDirection="column" justifyContent="flex-start">
      <Flex alignItems="center" justifyContent={['center', 'flex-end']}>
        <Flex flexDirection={['column', 'row']} gap={['s', 'xs']}>
          <Flex gap={['s', 'xs']}>
            <DropdownFilter
              multi={true}
              withCryptoIcon={true}
              filterField="selectedCollateralOption"
              options={data.extra?.collateral_assets}
              cryptoIconProps={{ network }}
              pagination={pagination}
              titleFormat={(filteredCount: number) =>
                `Collateral assets (${filteredCount})`
              }
              {...filters}
            />
            <DropdownFilter
              multi={true}
              withCryptoIcon={true}
              filterField="selectedDebtOption"
              options={data.extra?.debt_assets}
              cryptoIconProps={{ network }}
              pagination={pagination}
              titleFormat={(filteredCount: number) =>
                `Debt assets (${filteredCount})`
              }
              {...filters}
            />
          </Flex>
          <TextSearchFilter
            pagination={pagination}
            placeholder="Search by wallet"
            display="flex"
            flex="1"
            options={{
              variants: {
                textSearchFilter: 'fullWidth'
              }
            }}
            {...filters}
          />
        </Flex>
      </Flex>

      <DataTable
        rows={data.results}
        columns={columns}
        getKey={
          ((_: Liquidation, index: number) => index) as (
            row: Liquidation
          ) => string | number
        }
        rowLinkProps={{
          to: (row: TableData) => `/wallets/${row.wallet_address}`,
          // @ts-ignore
          linkComponent: Link
        }}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.pagination.total
        }}
        width="100%"
      />
    </Flex>
  );
}
